import {
  SkyBadge,
  SkyDataGrid,
  SkyProgressBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import type { CSSProperties } from "react";
import { Link } from "wouter";
import type { DbtJobRunRow } from "@shared/types/transformations";
import { StatusCell } from "@/components/runs/StatusCell";
import { pixelColumnLayout } from "@/lib/grid";
import { absolutePath } from "@/lib/routes";
import { skipReasonWord } from "@/lib/transformations/runOutcome";
import {
  kindLabel,
  runStartText,
  testsPair,
  testsSkipped,
  activeStepLine,
  transformStepPresentation,
} from "./transformFormatting";
import { zoneCaption } from "@/lib/time/displayTime";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { RunningDuration } from "@/components/runs/RunningDuration";

/**
 * The transform step of recent runs (canvas 16, Runs tab; FR-62, SEC-17).
 *
 * One row per run, every selector: the runner merges every enabled selector
 * into one dbt invocation, so the step is one step per run. The Error column is
 * `DbtJobRunRow.error`, the runner's own sentence ("The dbt run failed
 * in the clone phase.") — the screen never composes a cause of its own
 * and never shows dbt's message. The Cause column (S7) is the primary
 * classified cause's label and a "+N" for the other classified nodes; the
 * cause's sentence is already the end of the Error line, so it is not repeated.
 *
 * A step in flight gets a progress bar with its phase; the counts it reports
 * so far stay in the row.
 */

function countPair(ok: number | null, failed: number | null): string {
  if (ok === null && failed === null) return "—";
  return `${ok ?? 0} / ${failed ?? 0}`;
}

/** How many classified nodes besides the primary one (`causeCounts` summed, minus one). */
function otherCauses(row: DbtJobRunRow): number {
  const total = Object.values(row.causeCounts ?? {}).reduce((sum, count) => sum + (count ?? 0), 0);
  return Math.max(0, total - 1);
}

export interface DbtJobRunsGridProps {
  rows: DbtJobRunRow[];
  isLoading: boolean;
}

/**
 * Each column's floor in pixels: its header text plus the 32 px header padding, measured in
 * Chromium at 14 px (`pixelColumnLayout`, `client/src/lib/grid.ts`).
 */
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  runId: 168,
  kind: 112,
  stepStatus: 128,
  startedAt: 128,
  durationSeconds: 112,
  modelsReported: 208,
  testsFailed: 176,
  cause: 96,
  error: 160,
};

/** The tests cell's hover text: how many tests ran and how many were skipped, when known. */
function testsTitle(row: DbtJobRunRow): string | undefined {
  if (row.testsTotal === null) return undefined;
  const skipped = testsSkipped(row) ? `, ${testsSkipped(row)} skipped` : "";
  return `${row.testsTotal} tests${skipped}`;
}

export function DbtJobRunsGrid({ rows, isLoading }: DbtJobRunsGridProps) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<DbtJobRunRow>[] = [
    {
      key: "runId",
      header: "Run",
      width: "16%",
      renderCell: (_value, row) => (
        <Link
          href={absolutePath(`/runs/${row.runId}`)}
          className="underline-offset-2 hover:underline"
        >
          <SkyText size={200} font="monospace" weight="semibold" color="accent">
            {row.runId}
          </SkyText>
        </Link>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "8%",
      renderCell: (_value, row) => (
        <SkyText size={300} color="muted">
          {kindLabel(row.kind)}
        </SkyText>
      ),
    },
    {
      key: "stepStatus",
      header: "Status",
      width: "12%",
      renderCell: (_value, row) => {
        const presentation = transformStepPresentation(row);
        return (
          <StatusCell
            testId={`transformation-run-status-${row.runId}`}
            badge={
              <SkyBadge appearance="tint" color={presentation.color}>
                {presentation.label}
              </SkyBadge>
            }
            secondary={
              presentation.isActive ? (
                <>
                  <SkyProgressBar
                    value={row.phase ? 50 : 10}
                    thickness="small"
                    label={`Run ${row.runId}: ${presentation.label}, ${activeStepLine(row)}`}
                    className="w-24 max-w-full"
                  />
                  <SkyText size={200} color="muted">
                    {activeStepLine(row)}
                  </SkyText>
                </>
              ) : undefined
            }
          />
        );
      },
    },
    {
      key: "startedAt",
      header: "Started",
      width: "13%",
      renderCell: (_value, row) => (
        <SkyText size={200} block>
          {/* #1207 (G1 review): the run's own start, as the Runs list names the run. */}
          {runStartText(row, zone)}
        </SkyText>
      ),
    },
    {
      key: "durationSeconds",
      header: "Duration",
      width: "7%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          <RunningDuration
            seconds={row.durationSeconds}
            runningSince={row.stepStatus === "running" ? row.startedAt : null}
          />
        </SkyText>
      ),
    },
    {
      key: "modelsReported",
      // #1207 round 1: the page's word for success, never "ok".
      header: "Models succeeded / failed",
      width: "9%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {/* Round 6, T5: ok is the models that succeeded (an older row: reported minus failed). */}
          {countPair(dbtModelCountsOf(row)?.built ?? null, row.modelsFailed)}
        </SkyText>
      ),
    },
    {
      key: "testsFailed",
      header: "Tests passed / failed",
      width: "8%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric" title={testsTitle(row)}>
          {testsPair(row)}
          {testsSkipped(row) ? ` · ${testsSkipped(row)} skipped` : ""}
        </SkyText>
      ),
    },
    // #1207: no Exit class column. The Status column already says how the run ended in words
    // ("Tests failed", "Model failed"), and the exit class is a machine value.
    {
      // S7: the primary classified cause's LABEL, plus how many other nodes were classified.
      // Never the sentence: from S7 the Error line already ends with it (contract §3.5).
      key: "cause",
      header: "Cause",
      width: "9%",
      renderCell: (_value, row) => {
        if (!row.cause) {
          return (
            <SkyText size={200} color="muted">
              —
            </SkyText>
          );
        }
        const others = otherCauses(row);
        return (
          <div className="flex flex-wrap items-center gap-1" data-testid={`run-cause-${row.runId}`}>
            <SkyBadge appearance="tint" color="danger">
              {row.cause.label}
            </SkyBadge>
            {others > 0 ? (
              <SkyBadge
                appearance="outline"
                color="subtle"
                aria-label={`${others} other classified node${others === 1 ? "" : "s"}`}
              >
                {`+${others}`}
              </SkyBadge>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "error",
      header: "Error",
      width: "18%",
      renderCell: (_value, row) => (
        <SkyText size={200} color="muted" block>
          {row.error ?? (row.skippedReason ? `Skipped: ${skipReasonWord(row.skippedReason)}` : "—")}
        </SkyText>
      ),
    },
  ];

  // #1207: the grid.ts floor, as the History view has it, so a narrow page scrolls the grid
  // sideways instead of running one header into the next.
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    <section
      aria-label={`Recent runs of this dbt job. ${zoneCaption(zone)}.`}
      style={{ "--transformation-runs-floor": `${layout.floor}px` } as CSSProperties}
    >
      <SkyDataGrid<DbtJobRunRow>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.runId}
        loading={isLoading}
        emptyMessage="No dbt job has run yet."
        className="[&_table]:min-w-(--transformation-runs-floor)"
        testId="transformation-runs-grid"
      />
      <ZoneCaption testId="transformation-runs-zone-caption" className="mt-2" />
    </section>
  );
}
